'use client'

import { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { LargeUserIcon } from '@repo/ui/LargeUserIcon'
import { LargeHouseIcon } from '@repo/ui/LargeHouseIcon'
import { LargeContactDetailsIcon } from '@repo/ui/LargeContactDetailsIcon'
import { useCOISStore } from '@repo/store/useCOISStore'
import { validate } from '@repo/validation/validation'
import { coisPartyDetailsSchema } from '@repo/validation/schema_COIS'
import MUIStack from '@mui/material/Stack'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import { AddressInput } from '@repo/ui/AddressInput'
import { TextEntry } from '@repo/ui/TextEntry'

export const COISPartyDetails = ({ wideMode, handlePrevNext }) => {
  const { currentPartyId, parties, updateCOISWizardSidebar, updateParty } = useCOISStore()
  const currentParty = parties[currentPartyId]

  const formRef = useRef()
  const [partyType, setParytType] = useState(currentParty?.partyType || 'individual')
  const [forename, setForename] = useState(currentParty?.forename || '')
  const [surname, setSurname] = useState(currentParty?.surname || '')
  const [companyName, setCompanyName] = useState(currentParty?.companyName || '')
  const [phone1, setPhone1] = useState(currentParty?.phone1 || '')
  const [phone2, setPhone2] = useState(currentParty?.phone2 || '')
  const [emailAddress, setEmailAddress] = useState(currentParty?.emailAddress || '')
  const [house, setHouse] = useState(currentParty?.house || '')
  const [area, setArea] = useState(currentParty?.area || '')
  const [postalTown, setPostalTown] = useState(currentParty?.postalTown || '')
  const [county, setCounty] = useState(currentParty?.county || '')
  const [postcode, setPostcode] = useState(currentParty?.postcode || '')
  const [validationErrors, setValidationErrors] = useState({})

  const handleSubmit = async (e) => {
    e.preventDefault()
    setValidationErrors({})

    const prevState = parties[currentPartyId] || {}
    const fullName = `${forename ? forename + ' ' : ''}${surname}`
    const displayName = partyType === 'company' ? companyName : fullName

    const stateToSave = {
      ...prevState,
      partyType,
      forename,
      surname,
      companyName,
      phone1,
      phone2,
      emailAddress,
      house,
      area,
      postalTown,
      county,
      postcode,
      displayName
    }

    try {
      await validate(coisPartyDetailsSchema, stateToSave)
    } catch (err) {
      console.error(err)
      console.log(coisPartyDetailsSchema, stateToSave)
      const { inner } = err
      const errors = {}
      inner.forEach(({ path, message }) => {
        errors[path] = message
      })

      setValidationErrors(errors)
      return
    }

    Object.keys(stateToSave).forEach((key) => {
      updateParty({ partyId: currentPartyId, newFieldKey: key, newFieldValue: stateToSave[key] })
    })

    updateCOISWizardSidebar()
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    updateCOISWizardSidebar()
    handlePrevNext(-1)
  }

  const setAddress = ({ house, area, postalTown, county, postcode }) => {
    setHouse(house)
    setArea(area)
    setPostalTown(postalTown)
    setCounty(county)
    setPostcode(postcode)
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      <WizardPanel data-testid="COISPartyDetails">
        <MUIStack direction="column" gap="36px">
          <WizardFormSection>
            <WizardIconTitle title="Party details" icon={LargeUserIcon} sx={{ mb: 1 }} />
            <MUIRadioGroup
              value={partyType}
              name="partyType"
              row
              sx={{ width: '100%' }}
              onChange={(e) => setParytType(e.target.value)}
            >
              <MUIFormControlLabel value="individual" control={<MUIRadio />} label="Individual" />
              <MUIFormControlLabel value="company" control={<MUIRadio />} label="Company" />
            </MUIRadioGroup>
            {partyType === 'individual' ? (
              <TextEntry
                size="medium"
                name="forename"
                label="Forename"
                value={forename}
                onChange={setForename}
                errors={validationErrors}
                autoFocus
                sx={{ width: '100%', maxWidth: '460px' }}
              />
            ) : null}
            <TextEntry
              size="medium"
              name={partyType === 'individual' ? 'surname' : 'companyName'}
              label={partyType === 'individual' ? 'Surname' : 'Company name'}
              value={partyType === 'individual' ? surname : companyName}
              onChange={partyType === 'individual' ? setSurname : setCompanyName}
              errors={validationErrors}
              required={partyType === 'company'}
              sx={{ width: '100%', maxWidth: '460px' }}
            />
          </WizardFormSection>

          <WizardFormSection>
            <WizardIconTitle
              title="Contact details"
              icon={LargeContactDetailsIcon}
              sx={{ mb: 1 }}
              errors={validationErrors}
            />
            <TextEntry
              size="medium"
              name="phone1"
              label="Phone number 1"
              value={phone1}
              onChange={setPhone1}
              errors={validationErrors}
              sx={{ width: '100%', maxWidth: '460px' }}
            />
            <TextEntry
              size="medium"
              name="phone2"
              label="Phone number 2"
              value={phone2}
              onChange={setPhone2}
              errors={validationErrors}
              sx={{ width: '100%', maxWidth: '460px' }}
            />
            <TextEntry
              size="medium"
              name="emailAddress"
              label="Email"
              type="emailAddress"
              value={emailAddress}
              onChange={setEmailAddress}
              errors={validationErrors}
              sx={{ width: '100%', maxWidth: '460px' }}
            />
          </WizardFormSection>

          <MUIStack direction="column" gap="16px">
            <WizardIconTitle
              title="Address details"
              icon={LargeHouseIcon}
              sx={{ mb: 1, width: '100%', maxWidth: '640px' }}
            />

            <AddressInput
              validationErrors={validationErrors}
              onChange={setAddress}
              sx={{ width: '100%', maxWidth: '936px' }}
              defaultValue={{ house, area, postalTown, county, postcode }}
            />
          </MUIStack>
        </MUIStack>
      </WizardPanel>

      <WizardFooter wideMode={wideMode} handleClickBack={handleClickBack} continueButtonIsFormSubmitButton />
    </form>
  )
}

COISPartyDetails.propTypes = {
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func
}
